import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Alert, Box, Button, Grid, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";

import MapPicker from "../../../components/map/MapPicker";
import NeshanMapView from "../../../components/maps/NeshanMapView";

const AssistedRegistrationLocationSection = ({
  locationInput,
  onLocationInputChange,
  xPoint,
  yPoint,
  onCoordsChange,
  onParseLocation,
  locationError,
  locationParsing,
  mapError,
}) => {
  const [mapPickerOpen, setMapPickerOpen] = useState(false);
  const [tempMapPosition, setTempMapPosition] = useState(null);
  const [parseError, setParseError] = useState("");

  useEffect(() => {
    const input = locationInput?.trim();
    if (!input || !onParseLocation) {
      setParseError("");
      return undefined;
    }

    const timer = setTimeout(async () => {
      try {
        const result = await onParseLocation(input);
        if (result?.latitude != null && result?.longitude != null) {
          setParseError("");
          onCoordsChange({
            xPoint: Number(result.latitude),
            yPoint: Number(result.longitude),
            fromLink: true,
          });
        }
      } catch (err) {
        setParseError(err?.message || "امکان استخراج مختصات از لینک وجود ندارد.");
      }
    }, 600);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationInput]);

  const hasCoords = Boolean(xPoint && yPoint);
  const markerPosition = hasCoords ? [Number(xPoint), Number(yPoint)] : null;

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="subtitle2" gutterBottom>
          موقعیت محل نصب
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          می‌توانید لینک نقشه (گوگل‌مپ، نشان، واتساپ و …) را وارد کنید یا موقعیت را
          مستقیماً روی نقشه انتخاب کنید.
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <TextField
          fullWidth
          multiline
          minRows={2}
          label="لینک یا مختصات موقعیت"
          placeholder="لینک گوگل‌مپ، نشان، واتساپ/تلگرام یا lat,lng"
          value={locationInput}
          onChange={(e) => onLocationInputChange(e.target.value)}
          helperText={
            locationParsing
              ? "در حال بررسی لینک..."
              : "با وارد کردن لینک، موقعیت روی نقشه نمایش داده می‌شود."
          }
          error={Boolean(locationError || parseError)}
        />
        {locationError || parseError ? (
          <Alert severity="error" sx={{ mt: 1 }}>
            {locationError || parseError}
          </Alert>
        ) : null}
      </Grid>

      <Grid item xs={12} md={6}>
        <Button
          variant={hasCoords ? "contained" : "outlined"}
          color={hasCoords ? "success" : "primary"}
          fullWidth
          onClick={() => {
            if (hasCoords) {
              setTempMapPosition([Number(xPoint), Number(yPoint)]);
            }
            setMapPickerOpen(true);
          }}
          startIcon={hasCoords ? <CheckCircleIcon /> : <LocationOnIcon />}
          sx={{ mb: 1 }}
        >
          {hasCoords ? "موقعیت انتخاب شده — تغییر روی نقشه" : "انتخاب موقعیت روی نقشه"}
        </Button>
        {mapError ? (
          <Typography color="error" variant="caption" display="block" sx={{ mb: 1 }}>
            {mapError}
          </Typography>
        ) : null}
        {hasCoords ? (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            مختصات: {Number(xPoint).toFixed(6)}, {Number(yPoint).toFixed(6)}
          </Typography>
        ) : null}
        <Box sx={{ borderRadius: 1, overflow: "hidden", border: "1px solid", borderColor: "divider" }}>
          <NeshanMapView
            height={220}
            readOnly
            markerPosition={markerPosition}
            center={markerPosition || undefined}
            zoom={markerPosition ? 15 : undefined}
          />
        </Box>
      </Grid>

      <MapPicker
        open={mapPickerOpen}
        onClose={() => setMapPickerOpen(false)}
        initialPosition={tempMapPosition || (hasCoords ? [Number(xPoint), Number(yPoint)] : undefined)}
        onSelect={({ lat, lng, address }) => {
          onCoordsChange({
            xPoint: lat,
            yPoint: lng,
            address: address || undefined,
            fromLink: false,
          });
          if (!locationInput?.trim()) {
            onLocationInputChange(`${lat},${lng}`);
          }
        }}
      />
    </>
  );
};

export default AssistedRegistrationLocationSection;
